import React, { useState } from 'react';
import { Typography, Container } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './ProcessStepsSection.css';
import '../styles/SectionTitles.css';

const steps = [
  {
    number: '01',
    title: 'Discovery',
    desc: 'We analyze your business needs, market, and users to define the best solution.'
  },
  {
    number: '02',
    title: 'Strategy & Planning',
    desc: 'We craft a clear roadmap, set priorities, and define the project scope for success.'
  },
  {
    number: '03',
    title: 'Design',
    desc: 'Our team creates intuitive, engaging, and visually stunning user experiences.'
  },
  {
    number: '04',
    title: 'Development',
    desc: 'We build scalable, secure, and high-performance software using the latest technologies.'
  },
  {
    number: '05',
    title: 'Launch & Support',
    desc: 'We deploy your product, monitor performance, and provide ongoing support and improvements.'
  }
];

const descVariants = {
  hidden: { opacity: 0, y: 100 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { 
      duration: 0.6, 
      ease: 'easeOut' 
    } 
  },
};

const ProcessStepsSection = () => {
  const [openIdx, setOpenIdx] = useState(0);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  return (
    <div className="process-steps-root">
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="process-steps-header"
        >
          <div className="section-title-row">
            <Typography variant="h3" fontWeight={800} className="section-title-gradient">
              Our Proven Process
            </Typography>
            <span className="section-title-line" />
          </div>
          <Typography variant="h6" className="section-subtitle">
            We deliver innovative software, web, and mobile solutions through a proven, collaborative process—ensuring your product is robust, user-focused, and ready for growth.
          </Typography>
        </motion.div>
        
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={{
            hidden: {},
            visible: {
              transition: {
                staggerChildren: 0.2
              }
            }
          }}
          className="process-steps-tabs-wrapper"
          onMouseLeave={() => setOpenIdx(0)}
        >
          <div className="process-steps-tabs">
            {steps.map((step, idx) => (
              <motion.div
                key={step.number}
                variants={descVariants}
                className={`process-step-tab${openIdx === idx ? ' open' : ''}`}
                onMouseEnter={() => setOpenIdx(idx)}
              >
                <div className="process-step-left">
                  <div className="process-step-number">{step.number}</div>
                  <div className="process-step-title">{step.title}</div>
                </div>
                <div className="process-step-right">
                  <AnimatePresence mode="wait">
                    {openIdx === idx && (
                      <motion.div
                        className="process-step-desc"
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        variants={descVariants}
                        key={step.number}
                      >
                        {step.desc}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </Container>
    </div>
  );
};

export default ProcessStepsSection; 